export const themeColors = {
  DEFAULT: [
    '#EB9677',
    '#EACFC4',
    '#D8A927',
    '#F4E7C2',
    '#337A80',
    '#D3EBEE',
    '#8B9362',
    '#D3D6C2',
    '#C5DAD1',
    '#E6EFEB'
  ],
  SUNNY: [
    '#FCD639',
    '#FEF5CD',
    '#EC9647',
    '#F8D7BA',
    '#668BA6',
    '#DFE7EC',
    '#E4DBB5',
    '#F8F6EC',
    '#41596B',
    '#DFE6EC'
  ],
  MUTED: [
    '#9CAEBA',
    '#D2DAE0',
    '#8DA67F',
    '#D7E0D2',
    '#DAA85E',
    '#F1DDC1',
    '#E9E1D5',
    '#F6F3EE',
    '#50646F',
    '#E1E7EA'
  ],
  CHIC: [
    '#542495',
    '#E3D6F5',
    '#3E52E5',
    '#BCC3F6',
    '#F1C17D',
    '#FAE9D1',
    '#CFA5C3',
    '#E4CDDE',
    '#393235',
    '#CFC9CC'
  ],
  SMART: [
    '#02649C',
    '#9AD9FE',
    '#50B6F2',
    '#D0EBFB',
    '#72ADD0',
    '#D9E9F2',
    '#C3DBE9',
    '#ECF4F8',
    '#00273E',
    '#99D9FF'
  ],
  SPORTY: [
    '#013485',
    '#CCE0FF',
    '#F51B35',
    '#FB9DA8',
    '#F8C606',
    '#FDE99B',
    '#B6E00F',
    '#E7F99F',
    '#141414',
    '#CCCCCC'
  ],
  VERDANT: [
    '#1BA965',
    '#EFFCF6',
    '#75ABAC',
    '#EBFFFF',
    '#67D9C4',
    '#C4F8EE',
    '#F3F4C2',
    '#F8F9DC',
    '#3C4F45',
    '#EBF0ED'
  ],
  TRENDSETTING: [
    '#EE3B86',
    '#FFEBF3',
    '#E89739',
    '#F8E0C4',
    '#4656D1',
    '#DCE0F9',
    '#AA6EF7',
    '#E6DCF3',
    '#000000',
    '#EDEDED'
  ],
  GRAYSCALE: [
    '#000000',
    '#242424',
    '#3A3A3A',
    '#525252',
    '#707070',
    '#999999',
    '#B7B7B7',
    '#D5D5D5',
    '#EBEBEB',
    '#F4F4F4'
  ],
}

export const isLight = (hexColor = '#FFFFFF') => {
  if(!hexColor) return false
  // Remove the # character if it's present
  hexColor = hexColor.replace(/^#/, '')

  // Parse the hex color into RGB components
  const r = parseInt(hexColor.substring(0, 2), 16)
  const g = parseInt(hexColor.substring(2, 4), 16)
  const b = parseInt(hexColor.substring(4, 6), 16)

  // Calculate relative luminance using the formula for sRGB
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

  // Decide whether to use white or dark text based on luminance
  if (luminance > 0.5) {
    return true // Use white text on light background
  } else {
    return false // Use dark text on dark background
  }
}

const formatExtensionColors = () => {
  let themeObj = {}
  // Based on embeds-ui createExtensionColorClasses
  Object.keys(themeColors).map(themeKey => {
    themeObj[themeKey] = {}
    themeColors[themeKey].map((color, index) => {
      const firstKey = Math.round(((index + 1) / 2))
      const secondKey = (index + 1) % 2 === 0 ? 2 : 1
      const extensionKey = `${firstKey}_${secondKey}`
      themeObj[themeKey][extensionKey] = {
        background: color,
        color: isLight(color) ? '#000000' : '#FFFFFF' 
      }
    })
  })
  return themeObj
}

export const themeExtensionColors = formatExtensionColors()

// theme.colors = createThemeColors(isDarkMode, getHueFromHex(primaryColorHex))

const hslToHex = (h, s, l) => {
  l /= 100
  const a = (s * Math.min(l, 1 - l)) / 100
  const f = (n) => {
    const k = (n + h / 30) % 12
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0') // convert to Hex and prefix "0" if needed
  }
  return `#${f(0)}${f(8)}${f(4)}`
}

const createThemeHues = (hue, mono) => {
  return {
    _14: hslToHex(hue, mono ? 0 : 83, 14),
    _25: hslToHex(hue, mono ? 0 : 50, 25),
    _32: hslToHex(hue, mono ? 0 : 33, 32),
    _50: hslToHex(hue, mono ? 0 : 33, 50),
    _61: hslToHex(hue, mono ? 0 : 33, 61),
    _61a: hslToHex(hue, mono ? 0 : 66, 61),
    _69: hslToHex(hue, mono ? 0 : 33, 69),
    _76: hslToHex(hue, mono ? 0 : 33, 76),
    _85: hslToHex(hue, mono ? 0 : 33, 85),
    _94: hslToHex(hue, mono ? 0 : 50, 94),
    _98: hslToHex(hue, mono ? 0 : 50, 98)  
  }
}

export const createThemeColors = (
  isDark = false,
  hue = 228,
  mono = false,
  accent = null
) => {
  const hues = createThemeHues(hue, mono)
  return isDark
    ? createDarkThemeColors(hues, accent)
    : createLightThemeColors(hues, accent)
}


export const createLightThemeColors = (hues, accent) => {
  const bodyText = '#000000'
  const neutral = '#ffffff'
  const gray = {
    main: '#606060',
    secondary: '#DFDFDF'
  }
  const transparent = 'transparent'
  const focusBlue = '#3257EC'
  const lightModeBackground = '#ffffff'

  const red = '#DF1D1D'

  return {
    primary: hues._14,
    secondary: hues._14,
    defaultBackground: lightModeBackground,
    accent: accent || hues._61a, // Not sure which one should be used here
    'neutrals.bodyText': bodyText,
    'neutrals.neutral': neutral,
    'neutrals.gray.main': gray.main,
    'neutrals.gray.secondary': gray.secondary,
    'neutrals.transparent': transparent,
    'system.red': red,
    'system.focusBlue': focusBlue,
    headerText: hues._14,
    secondaryText: hues._50,
    tertiaryText: hues._69,
    backgroundPrimary: hues._98,
    outlinePrimary: hues._76,
    backgroundSecondary: hues._94,
    outlineSecondary: hues._61,
    backgroundTertiary: hues._94,
    outlineTertiary: hues._85,
    inputText: gray.main,
    buttons: {
      primary: {
        main: { background: hues._14, text: neutral },
        hover: { background: bodyText, text: hues._76 },
        focused: { background: hues._14, text: neutral, border: focusBlue },
        pressed: { background: hues._61, text: neutral },
        selected: { background: hues._94, text: hues._14 },
        disabled: { background: hues._85, text: hues._61 }
      },
      secondary: {
        main: { background: hues._94, text: hues._14 },
        hover: { background: hues._98, text: hues._14 },
        focused: { background: hues._94, text: hues._14, border: focusBlue },
        pressed: { background: hues._61, text: hues._14 },
        selected: { background: hues._76, text: hues._14 },
        disabled: { background: gray.secondary, text: neutral }
      }
    },
    'tooltips.background': bodyText,
    'tooltips.text': neutral
  }
}


export const createDarkThemeColors = (hues, accent) => {
  const bodyText = '#ffffff'
  const neutral = '#000000'
  const gray = {
    main: '#DFDFDF',
    secondary: '#606060'
  }

  const transparent = 'transparent'
  const red = '#DF1D1D'
  const focusBlue = '#3257EC'
  const darkModeBackground = '#282828'

  return {
    primary: hues._50,
    secondary: hues._69,
    defaultBackground: darkModeBackground,
    accent: accent || hues._61a,
    'neutrals.bodyText': bodyText,
    'neutrals.neutral': neutral,
    'neutrals.gray.main': gray.main,
    'neutrals.gray.secondary': gray.secondary,
    'neutrals.transparent': transparent,
    'system.red': red,
    'system.focusBlue': focusBlue,
    headerText: hues._94,
    secondaryText: hues._76,
    tertiaryText: hues._61,
    backgroundPrimary: hues._14,
    outlinePrimary: hues._61,
    backgroundSecondary: hues._32,
    outlineSecondary: hues._50,
    backgroundTertiary: hues._14,
    outlineTertiary: hues._85,
    inputText: gray.main,
    buttons: {
      primary: {
        main: { background: hues._50, text: bodyText },
        hover: { background: hues._32, text: hues._76 },
        focused: { background: hues._25, text: bodyText, border: focusBlue },
        pressed: { background: hues._61, text: bodyText },
        selected: { background: hues._25, text: bodyText },
        disabled: { background: hues._61, text: hues._85 }
      },
      secondary: {
        main: { background: hues._61, text: hues._14, border: transparent },
        hover: { background: hues._50, text: hues._14, border: transparent },
        focused: { background: hues._61, text: hues._14, border: focusBlue },
        pressed: { background: hues._61, text: hues._14, border: transparent },
        selected: { background: hues._76, text: hues._14, border: transparent },
        disabled: {
          background: gray.secondary,
          text: neutral,
          border: transparent
        }
      }
    },
    'tooltips.background': bodyText,
    'tooltips.text': neutral
  }
}


export const getHueFromHex = (hex = '#061240') => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '#061240') || '200'

  let r = parseInt(result[1], 16)
  let g = parseInt(result[2], 16)
  let b = parseInt(result[3], 16)

  r /= 255
  g /= 255
  b /= 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = (max + min) / 2

  if (max === min) {
    h = 0 // achromatic
  } else {
    const d = max - min
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }

    h /= 6
  }

  h = Math.round(h * 360)

  return h
}

export const isValidHexColor = (color) => {
  // Regular expression pattern to match hexadecimal color code
  var hexColorPattern = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

  // Check if the color matches the pattern
  return hexColorPattern.test(color);
}

export const isHexColorMuted = hexColor => {
  if(!hexColor) return false
  // Convert hexadecimal to RGB
  const red = parseInt(hexColor.substring(1, 3), 16) / 255;
  const green = parseInt(hexColor.substring(3, 5), 16) / 255;
  const blue = parseInt(hexColor.substring(5, 7), 16) / 255;

  // Find the maximum and minimum values of RGB components
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);

  // Calculate the lightness
  const lightness = (max + min) / 2;

  // Calculate the saturation
  let saturation = 0;
  if (max !== min) {
      var delta = max - min;
      saturation = delta / (1 - Math.abs(2 * lightness - 1));
  }
  return saturation < 0.2
}